import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api-client';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import { writeConsoleMapping } from '@/hooks/useAddressValidation';
import { PostalCodeLocalityHint } from '@/components/tickets/PostalCodeLocalityHint';
import {
  declaredOverlay, duplicatesOfInput, houseNumberDigitsOnly, houseNumberExtensionCoherent,
  type DeclaredUnit, type DmpConsoleMappingResponse, type DmpConsoleMappingRow, type DmpLookupResponse,
} from '@uniticket/shared';

/**
 * N39 (décisions du user du 08-10) — créer ou modifier, depuis la console DMP, ce qu'UN opérateur déclare pour un
 * JVID. Admins seuls : la page ne rend ce formulaire que pour eux, et le serveur garde la route de son côté.
 *
 *  · création : l'opérateur se CHOISIT, sans valeur par défaut (un opérateur pré-coché finirait par recevoir les
 *    déclarations des autres) ; un opérateur qui a déjà sa ligne n'est pas proposé. Les champs partent de
 *    l'adresse du DMP — la déclaration est le plus souvent cette adresse, à un détail près.
 *  · modification : l'opérateur est fixé, les champs partent de la ligne ; « Enregistrer » ne s'allume qu'une
 *    fois un champ changé. La ligne porte son jeton (`updatedAtToken`) : si elle a bougé entre-temps, le serveur
 *    refuse et le message le dit.
 * Pas de motif (décision du user) : l'écriture est tracée à l'audit et au journal d'actions.
 * L'avertissement de doublon (N38) paraît pendant la saisie, par opérateur, et ne bloque rien.
 */

const FIELDS = [
  { key: 'streetName', labelKey: 'fields.street', required: true },
  { key: 'houseNumber', labelKey: 'fields.houseNumber', required: true },
  { key: 'houseNumberExtension', labelKey: 'fields.houseNumberExt', required: false },
  { key: 'busNumber', labelKey: 'fields.busNumber', required: false },
  { key: 'postalCode', labelKey: 'fields.postalCode', required: true },
  { key: 'municipality', labelKey: 'fields.municipality', required: true },
  { key: 'city', labelKey: 'fields.city', required: true },
] as const;

type Key = typeof FIELDS[number]['key'];

export function ConsoleMappingForm({
  address,
  mapping,
  row,
  onSaved,
}: {
  address: DmpLookupResponse;
  mapping: DmpConsoleMappingResponse;
  /** La ligne à modifier — `null` = créer la déclaration d'un opérateur. */
  row: DmpConsoleMappingRow | null;
  onSaved: (jvid: number) => void;
}) {
  const { t } = useTranslation(['tickets']);
  const confirmDmpWrite = useDmpWriteConfirm();
  const ref = address.reference;
  const source = (row ?? ref) as unknown as Record<Key, string | null>;
  const initial = Object.fromEntries(FIELDS.map((f) => [f.key, (source[f.key] ?? '').trim()])) as Record<Key, string>;

  const [form, setForm] = useState<Record<Key, string>>(initial);
  // Création : aucun opérateur choisi au départ. Modification : celui de la ligne.
  const [operatorId, setOperatorId] = useState<number | null>(row ? row.operatorId : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const operator = mapping.operators.find((o) => o.operatorId === operatorId) ?? null;
  const taken = new Set(mapping.rows.map((r) => r.operatorId));
  const changed = FIELDS.filter((f) => form[f.key].trim() !== initial[f.key]);
  const missing = FIELDS.filter((f) => f.required && form[f.key].trim() === '');
  const notDigits = form.houseNumber.trim() !== '' && !houseNumberDigitsOnly(form.houseNumber);
  const incoherent = !houseNumberExtensionCoherent(form.houseNumber, form.houseNumberExtension);

  // N38 — les AUTRES logements du bâtiment, tels que CET opérateur les voit (sa déclaration, sinon le DMP).
  const units: DeclaredUnit[] = operatorId == null ? [] : mapping.siblings.map((s) => {
    const declared = s.declarations.find((d) => d.operatorId === operatorId) ?? null;
    return {
      jvid: s.jvid,
      streetName: s.streetName,
      houseNumber: s.houseNumber,
      houseNumberExtension: s.houseNumberExtension,
      busNumber: s.busNumber,
      ...declaredOverlay(s, declared),
      ...(declared ? { mappedBusNumber: declared.busNumber ?? '' } : {}),
    };
  });
  const twins = duplicatesOfInput(units, mapping.jvid, form);

  const canSave = !saving && operator != null && missing.length === 0 && !notDigits && !incoherent && (row == null || changed.length > 0);

  async function save() {
    if (!canSave || !operator) return;
    if (!confirmDmpWrite(t('tickets:dmpLookup.mapping.form.confirmAction', { jvid: mapping.jvid }))) return;
    setSaving(true);
    setError(null);
    try {
      await writeConsoleMapping(mapping.jvid, {
        operatorCode: operator.code,
        mode: row ? 'update' : 'create',
        ...(row ? { expectedUpdatedAt: row.updatedAtToken } : {}),
        streetName: form.streetName.trim(),
        houseNumber: form.houseNumber.trim(),
        houseNumberExtension: form.houseNumberExtension.trim(),
        busNumber: form.busNumber.trim(),
        postalCode: form.postalCode.trim(),
        municipality: form.municipality.trim(),
        city: form.city.trim(),
      });
      toast.success(t(row ? 'tickets:dmpLookup.mapping.form.doneEdit' : 'tickets:dmpLookup.mapping.form.doneCreate', { operator: operator.label }));
      onSaved(mapping.jvid);
    } catch (err) {
      // Les trois refus que le serveur nomme se disent dans nos mots ; le reste est rendu tel quel.
      const code = err instanceof ApiError ? (err.body?.code as string | undefined) : undefined;
      if (code === 'MAPPING_EXISTS') setError(t('tickets:dmpLookup.mapping.form.errExists'));
      else if (code === 'MAPPING_MOVED') setError(t('tickets:dmpLookup.mapping.form.errMoved'));
      else if (code === 'MAPPING_WRITE_DISABLED') setError(t('tickets:dmpLookup.mapping.form.errWriteOff'));
      else setError((err as Error)?.message || t('tickets:dmpLookup.mapping.form.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h3 className="mb-1 text-sm font-bold text-slate-800">
        {row
          ? t('tickets:dmpLookup.mapping.form.titleEdit', { operator: row.operatorLabel })
          : t('tickets:dmpLookup.mapping.form.titleCreate')}
      </h3>
      <p className="mb-3 text-[11px] text-slate-400">{t('tickets:dmpLookup.mapping.form.subtitle', { jvid: mapping.jvid })}</p>

      {!row && (
        <div className="mb-3">
          <label className="mb-1 block text-xs text-slate-500" htmlFor="map-operator">
            {t('tickets:dmpLookup.mapping.form.operator')}<span className="ml-0.5 text-rose-600">*</span>
          </label>
          <select
            id="map-operator"
            value={operatorId ?? ''}
            onChange={(e) => setOperatorId(e.target.value === '' ? null : Number(e.target.value))}
            disabled={saving}
            className={cn(
              'h-9 w-full rounded-md border bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-rose/40',
              operatorId == null ? 'border-rose-300' : 'border-slate-200',
            )}
          >
            <option value="">{t('tickets:dmpLookup.mapping.form.operatorPlaceholder')}</option>
            {mapping.operators.map((o) => (
              <option key={o.operatorId} value={o.operatorId} disabled={taken.has(o.operatorId)}>
                {taken.has(o.operatorId) ? t('tickets:dmpLookup.mapping.form.operatorTaken', { label: o.label }) : o.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-slate-400">{t('tickets:dmpLookup.mapping.form.prefilledDmp')}</p>
        </div>
      )}

      <div className="space-y-2.5">
        {FIELDS.map((f, i) => {
          const moved = form[f.key].trim() !== initial[f.key];
          const blank = f.required && form[f.key].trim() === '';
          const wrong = (f.key === 'houseNumber' && (notDigits || incoherent)) || (f.key === 'houseNumberExtension' && incoherent);
          return (
            <div key={f.key} className="grid grid-cols-[110px_1fr] items-center gap-2">
              <label className="text-xs text-slate-500" htmlFor={`map-${f.key}`}>
                {t(`tickets:validation.${f.labelKey}`)}
                {f.required && <span className="ml-0.5 text-rose-600">*</span>}
              </label>
              <Input
                id={`map-${f.key}`}
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                disabled={saving}
                // Même parade que « Modifier l'adresse » contre le remplissage automatique du navigateur : ce
                // formulaire écrit les adresses du DMP, pas celle de l'admin.
                autoComplete="new-password"
                name={`dmp-m-${i}`}
                data-1p-ignore
                data-lpignore="true"
                className={
                  blank || wrong ? 'h-8 text-sm bg-rose-50 border-rose-300'
                    : moved ? 'h-8 text-sm bg-amber-50 border-amber-300'
                      : 'h-8 text-sm'
                }
              />
            </div>
          );
        })}
      </div>

      <div className="mt-3">
        <PostalCodeLocalityHint
          postalCode={form.postalCode}
          city={form.city}
          active={form.postalCode.trim() !== initial.postalCode}
          disabled={saving}
          onPick={(c, m) => setForm((prev) => ({ ...prev, city: c, municipality: m }))}
        />
      </div>

      {missing.length > 0 && (
        <p className="mt-2 text-[11px] font-medium text-rose-600" role="alert">{t('tickets:dmpLookup.mapping.form.requiredBlank')}</p>
      )}
      {notDigits && (
        <p className="mt-2 text-[11px] font-medium text-rose-600" role="alert">{t('tickets:addressMgmt.create.errHouseNumberDigits')}</p>
      )}
      {incoherent && !notDigits && form.houseNumber.trim() !== '' && (
        <p className="mt-2 text-[11px] font-medium text-rose-600" role="alert">{t('tickets:validation.houseExtIncoherent')}</p>
      )}
      {/* N38 — même adresse qu'un autre logement pour cet opérateur : dit pendant la saisie, jamais bloquant. */}
      {twins.length > 0 && (
        <p className="mt-2 text-[11px] font-medium text-rose-600" role="alert">
          {t('tickets:validation.declaredDup.formWarning', { count: twins.length, jvids: twins.join(', ') })}
        </p>
      )}
      {error && <p className="mt-2 text-[11px] font-medium text-rose-600" role="alert">{error}</p>}

      <Button
        onClick={save}
        disabled={!canSave}
        className="mt-4 w-full bg-brand-gradient text-white hover:opacity-90"
      >
        {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
        {saving
          ? t('tickets:dmpLookup.mapping.form.saving')
          : row
            ? (changed.length === 0 ? t('tickets:dmpLookup.mapping.form.noChange') : t('tickets:dmpLookup.mapping.form.save'))
            : (operator == null ? t('tickets:dmpLookup.mapping.form.chooseOperator') : t('tickets:dmpLookup.mapping.form.create'))}
      </Button>
    </div>
  );
}
